/**
 * Quick Customization — Live Chat integration.
 *
 * On mobile collection pages with the QC sidebar, the third-party
 * floating chat widget (Tawk / Intercom) overlaps the bottom-sheet UI
 * and obscures the swatches / inputs. This module:
 *
 *  1. Hides the floating chat widget WHILE the bottom-sheet is open
 *     (the inline "Chat" pill takes over chat access in that state)
 *     and RESTORES it when the shopper collapses the sheet back to
 *     just the launcher pill — so they always have a way to start a
 *     chat session, with no double-button overlap.
 *
 *     Hiding combines two detection passes (named selectors for known
 *     vendors + a position heuristic for unknown ones) and three
 *     robustness layers (MutationObserver + ~30s polling + resize
 *     listeners). Each hide stores the element's original `style`
 *     attribute on a data-attribute so we can restore EXACTLY what
 *     the vendor put there (transforms, animation positions, etc).
 *
 *  2. Surfaces an inline "Chat" pill inside the sidebar's launcher
 *     row (markup in quick-customization.liquid). The pill stays
 *     hidden until Tawk_API.getStatus() reports "online", and re-hides
 *     on offline status. Tapping it calls Tawk_API.maximize() to open
 *     the chat session — same behavior as the floating bubble.
 *
 * Sheet-state coupling: a MutationObserver on the sidebar's `class`
 * attribute mirrors the `is-mobile-open` state onto `body` as
 * `qc-sheet-open`. The CSS hide-rule in quick-customization-mobile.liquid
 * keys off the body class for the initial paint; JS handles late-loaded
 * widgets and vendor inline-style overrides.
 *
 * Stays self-contained — no exports, no dependency on the other
 * team-design.* scripts beyond the shared DOM. Safe to load with
 * `defer`; runs on DOMContentLoaded if the document hasn't finished
 * parsing yet. The Tawk embed script itself is loaded by an external
 * channel (Shopify app / theme.liquid); this file only listens on the
 * `window.Tawk_API` global — no embed-script management here.
 */
(function () {
  'use strict';

  // ─── Selectors ──────────────────────────────────────────────────────────
  // Named-selector pass — covers Tawk + Intercom wrapper class/id
  // patterns we've seen. Vendors evolve their DOM, so we ALSO fall
  // back to a heuristic pass that hides any fixed-position iframe
  // pinned near the viewport's bottom-right corner (see hideByPosition
  // below) — the universal "floating chat widget" signature.
  var NAMED_CHAT_SELECTORS = [
    'iframe[title*="chat" i]',
    'iframe[title*="widget" i]',
    'iframe[src*="tawk"]',
    'iframe[src*="intercom"]',
    'iframe[src*="crisp"]',
    'iframe[id*="tawk" i]',
    'iframe[class*="tawk" i]',
    'iframe[name*="tawk" i]',
    '[id*="tawk" i]',
    '[class*="tawk" i]',
    'iframe[name="intercom-launcher-frame"]',
    '.intercom-launcher-frame',
    '.intercom-lightweight-app-launcher',
    '#tawk-bubble-container',
  ].join(',');

  // ─── Floating-widget hider ──────────────────────────────────────────────
  // Apply `display: none` inline (with priority) so we beat the
  // vendor's own inline styles. The body-class CSS rule does the same
  // for the initial paint; this is the runtime safety net for late-
  // loaded or mutation-restored elements.
  //
  // We remember each element's original `style` attribute on a data-
  // attribute so we can restore EXACTLY what was there when the sheet
  // closes (vendors care about their own inline transform / right /
  // bottom values for animations — we mustn't lose them).
  var hiddenNodes = []; // every element we've nuked since init
  function nukeNode(el) {
    if (!el || !el.style) return;
    if (!el.hasAttribute('data-qc-orig-style')) {
      el.setAttribute('data-qc-orig-style', el.getAttribute('style') || '');
      hiddenNodes.push(el);
    }
    el.style.setProperty('display',        'none',   'important');
    el.style.setProperty('visibility',     'hidden', 'important');
    el.style.setProperty('opacity',        '0',      'important');
    el.style.setProperty('pointer-events', 'none',   'important');
  }
  function restoreNode(el) {
    if (!el || !el.hasAttribute('data-qc-orig-style')) return;
    var original = el.getAttribute('data-qc-orig-style') || '';
    if (original) el.setAttribute('style', original);
    else          el.removeAttribute('style');
    el.removeAttribute('data-qc-orig-style');
  }
  function restoreAllFloatingChat() {
    for (var i = 0; i < hiddenNodes.length; i++) restoreNode(hiddenNodes[i]);
    hiddenNodes.length = 0;
  }

  // Walks up from `el` to find the nearest fixed / sticky ancestor and
  // hides it. Chat widgets typically wrap their iframe inside a
  // fixed-positioned `<div>`; hiding only the iframe leaves the
  // wrapper visible (and clickable) so we have to nuke the wrapper too.
  function nukeFixedAncestor(el) {
    var node  = el && el.parentElement;
    var depth = 0;
    while (node && node !== document.body && depth < 5) {
      var cs = node.ownerDocument.defaultView && node.ownerDocument.defaultView.getComputedStyle
        ? node.ownerDocument.defaultView.getComputedStyle(node) : null;
      if (cs && (cs.position === 'fixed' || cs.position === 'sticky')) {
        nukeNode(node);
        return;
      }
      node = node.parentElement;
      depth++;
    }
  }

  // Pass 1 — named selectors (Tawk / Intercom / Crisp patterns).
  function hideByName() {
    var nodes = document.querySelectorAll(NAMED_CHAT_SELECTORS);
    for (var i = 0; i < nodes.length; i++) {
      nukeNode(nodes[i]);
      nukeFixedAncestor(nodes[i]);
    }
  }

  // Pass 2 — position heuristic. We use getBoundingClientRect() (not
  // `bottom` from computed style) so we catch vendors that position
  // their bubble via `transform: translateY()` or absolute `top` math
  // rather than the textbook `bottom: 20px`. Anything fixed/sticky
  // that renders ≤ 150px from the viewport's bottom edge and is sized
  // like a chat bubble (≤ 200×200px) gets nuked. Skips elements
  // inside our own sidebar so we never blow away QC's UI.
  function isLikelyChatBubble(el) {
    var cs = window.getComputedStyle(el);
    if (cs.position !== 'fixed' && cs.position !== 'sticky') return false;
    var rect = el.getBoundingClientRect();
    if (rect.width  <= 0   || rect.height <= 0)   return false;
    if (rect.width  > 200  || rect.height > 200)  return false;
    var fromBottom = window.innerHeight - rect.bottom;
    return fromBottom > -50 && fromBottom < 150;
  }

  function hideByPosition() {
    var sidebar = document.querySelector('[class^="qc-sidebar-"], [class*=" qc-sidebar-"]');
    // Iframes are the standard chat-widget container, but Tawk's newer
    // builds also ship a non-iframe `<div>` bubble — query both so we
    // can catch either flavor with the same heuristic.
    var nodes = document.querySelectorAll('iframe, body > div');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (sidebar && (el === sidebar || sidebar.contains(el))) continue;
      // Skip our own pill / launcher row (they're never direct body
      // children, but defensive belt-and-suspenders).
      if (el.closest && el.closest('[class^="qc-sidebar-"]')) continue;
      if (isLikelyChatBubble(el)) {
        nukeNode(el);
        nukeFixedAncestor(el);
      }
    }
  }

  function nukeFloatingChat() {
    hideByName();
    hideByPosition();
  }

  // Hiding only runs on MOBILE viewports while the sheet is OPEN.
  // On desktop the sidebar is a sticky column (no bottom-sheet, no
  // overlap with the floating chat), so we must never touch the chat
  // widget there — even though `is-mobile-open` is set on the
  // sidebar (banner-swatches.js's IntersectionObserver doesn't check
  // viewport when toggling that class).
  var MOBILE_MQ = window.matchMedia('(max-width: 879px)');
  function isMobileViewport() { return MOBILE_MQ.matches; }
  function sheetIsOpen() {
    return document.body.classList.contains('qc-sheet-open');
  }
  function maybeNuke() {
    if (isMobileViewport() && sheetIsOpen()) nukeFloatingChat();
  }

  // Tawk loads async, so the iframe is usually NOT in the DOM when
  // this script runs. Combine three strategies for robustness — but
  // all three gate on the sheet's open state via maybeNuke:
  //   1. MutationObserver — catches new elements + vendor style/class
  //      mutations (some builds re-show themselves after display:none).
  //   2. Short interval poll — for the first 30s after init, in case
  //      the observer misses an injection.
  //   3. resize / orientationchange listeners — chat widgets sometimes
  //      respawn themselves on viewport changes.
  function startHidingFloatingChat() {
    maybeNuke();

    if (typeof MutationObserver !== 'undefined') {
      var obs = new MutationObserver(maybeNuke);
      obs.observe(document.body, {
        childList:      true,
        subtree:        true,
        attributes:     true,
        attributeFilter:['style', 'class'],
      });
    }

    var polls = 0;
    var pollTimer = setInterval(function () {
      maybeNuke();
      polls++;
      if (polls > 60) clearInterval(pollTimer); // ~30s of polling
    }, 500);

    window.addEventListener('resize',            maybeNuke);
    window.addEventListener('orientationchange', maybeNuke);
  }

  // Mirrors the sidebar's `is-mobile-open` class onto `body` as
  // `qc-sheet-open` — but ONLY on mobile viewports. On desktop the
  // sidebar is a normal column and the floating chat widget should
  // stay visible. Reacts to:
  //   - sidebar class changes (sheet open/close)         via MutationObserver
  //   - viewport mobile ↔ desktop transitions            via matchMedia
  // On any transition the right side effect runs:
  //   - mobile + open  : hide the floating chat widget
  //   - any other state: restore the floating chat widget
  // Using a MutationObserver on the sidebar's class attribute keeps
  // this module decoupled from banner-swatches.js (no event channel
  // to maintain, no hardcoded hooks across files).
  function watchSheetState(sidebar) {
    function sync() {
      var mobileOpen = isMobileViewport() && sidebar.classList.contains('is-mobile-open');
      var was        = document.body.classList.contains('qc-sheet-open');
      document.body.classList.toggle('qc-sheet-open', mobileOpen);
      if (mobileOpen && !was) {
        nukeFloatingChat();
      } else if (!mobileOpen && was) {
        restoreAllFloatingChat();
      }
    }
    sync();
    if (typeof MutationObserver !== 'undefined') {
      var obs = new MutationObserver(sync);
      obs.observe(sidebar, { attributes: true, attributeFilter: ['class'] });
    }
    // Re-sync on viewport changes — a mobile → desktop resize while
    // the sheet was open needs to restore the chat widget, and a
    // desktop → mobile resize with grid in view needs to hide it.
    if (MOBILE_MQ.addEventListener) {
      MOBILE_MQ.addEventListener('change', sync);
    } else if (MOBILE_MQ.addListener) {
      MOBILE_MQ.addListener(sync); // Safari < 14
    }
  }

  // ─── Inline Chat pill ───────────────────────────────────────────────────
  function bindChatPill(sidebar) {
    var btn = sidebar.querySelector('.qc-chat-btn');
    if (!btn) return;

    btn.addEventListener('click', function () {
      try {
        if (window.Tawk_API && typeof window.Tawk_API.maximize === 'function') {
          window.Tawk_API.maximize();
        }
      } catch (e) { /* swallow — widget unavailable */ }
    });

    function reveal(online) {
      if (online) btn.removeAttribute('hidden');
      else        btn.setAttribute('hidden', '');
    }

    // Simple status poll instead of wrapping Tawk_API.onLoad /
    // onStatusChange. Header.liquid's `initTawk()` also assigns those
    // callbacks AND re-runs after 2s via `setTimeout(init, 2000)`, so
    // a wrapper-based approach races on the assignment order and our
    // wrapper can get overwritten. Polling getStatus() every 2s
    // sidesteps that entirely: it's ~10 reads/min of cost for a
    // single function call, and reflects status changes within 2s.
    function checkStatus() {
      var Tawk = window.Tawk_API;
      if (Tawk && typeof Tawk.getStatus === 'function') {
        reveal(Tawk.getStatus() === 'online');
      }
      // If getStatus isn't available yet (Tawk embed still loading),
      // leave the button in its current state — `hidden` by default
      // from the markup, so it stays invisible until Tawk reports.
    }
    checkStatus();
    setInterval(checkStatus, 2000);
  }

  // ─── Init ───────────────────────────────────────────────────────────────
  function init() {
    var sidebar = document.querySelector('[class^="qc-sidebar-"], [class*=" qc-sidebar-"]');
    if (!sidebar) return; // no QC on this page — leave the floating widget alone
    watchSheetState(sidebar);
    startHidingFloatingChat();
    bindChatPill(sidebar);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
